import React from 'react';
import { readerStatusColors } from './colors';

export interface DonutSegment {
  key: string;
  value: number;
  color: string;
}

interface StatusDonutProps {
  segments: DonutSegment[];
  /** Rendered in the hole. Kept as children so each donut can put its own two lines there. */
  children?: React.ReactNode;
  size?: number;
  /** Draw arcs once the widget enters the scroll viewport. */
  animate?: boolean;
  /** Accessible one-line description; the SVG itself is decorative next to the legend. */
  label: string;
}

const VIEWBOX = 100;
const RADIUS = 38;
const STROKE = 16;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * A donut drawn as one arc per segment on a single circle.
 *
 * Hand-rolled rather than a chart library: at this size there is no axis, no tooltip and no responsive
 * behaviour to inherit, and stroke-dasharray gives crisper arc joins than a 4-slice pie would. The ring starts
 * at twelve o'clock and runs clockwise, which is the order the legend beside it lists.
 */
const StatusDonut: React.FC<StatusDonutProps> = ({ segments, children, size = 132, label, animate = true }) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  let sweptSoFar = 0;
  const arcs = segments
    .filter((segment) => segment.value > 0)
    .map((segment) => {
      const length = (segment.value / total) * CIRCUMFERENCE;
      const arc = { ...segment, length, offset: sweptSoFar };
      sweptSoFar += length;
      return arc;
    });

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} role="img" aria-label={label}>
      <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} className="h-full w-full -rotate-90">
        {/* The track stays visible while the arcs draw, and remains when there are no values. */}
        <circle
          cx={VIEWBOX / 2}
          cy={VIEWBOX / 2}
          r={RADIUS}
          fill="none"
          stroke={readerStatusColors.track}
          strokeWidth={STROKE}
        />

        {arcs.map((arc, index) => (
          <circle
            key={arc.key}
            cx={VIEWBOX / 2}
            cy={VIEWBOX / 2}
            r={RADIUS}
            fill="none"
            stroke={arc.color}
            strokeWidth={STROKE}
            className="motion-safe:transition-[stroke-dasharray] motion-safe:duration-500 motion-safe:ease-out"
            style={{
              strokeDasharray: `${animate ? arc.length : 0} ${animate ? CIRCUMFERENCE - arc.length : CIRCUMFERENCE}`,
              transitionDelay: `${index * 60}ms`
            }}
            strokeDashoffset={-arc.offset}
          />
        ))}
      </svg>

      {children && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center leading-tight">
          {children}
        </div>
      )}
    </div>
  );
};

export default StatusDonut;
